import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, Chip } from '@mui/material';
import CookieOutlinedIcon from '@mui/icons-material/CookieOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {
  hasConsentDecision,
  acceptAllCookies,
  rejectOptionalCookies
} from '../../../utils/cookieUtils';
import { initPostHog } from '../../../hooks/usePostHog';
import { removeAnalyticsScripts } from '../../../utils/scriptLoadingUtils';
import CookieDetailModal from './CookieDetailModal';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    const hasDecision = hasConsentDecision();
    setIsVisible(!hasDecision);
  }, []);

  const handleAcceptAll = async () => {
    acceptAllCookies();
    setIsVisible(false);

    try {
      await initPostHog();
    } catch (error) {
      // Silent initialization failure
    }
  };

  const handleRejectOptional = () => {
    rejectOptionalCookies();
    removeAnalyticsScripts();
    setIsVisible(false);
  };

  const handleOpenSettings = () => {
    setSettingsOpen(true);
  };

  const handleCloseSettings = (wasSaved) => {
    setSettingsOpen(false);
    if (wasSaved) {
      setIsVisible(false);
    }
    // If dismissed without saving, banner remains visible
  };

  return (
    <>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          backgroundColor: '#fff',
          borderTop: '3px solid #AF3241',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          display: isVisible && !settingsOpen ? 'block' : 'none',
        }}
      >
        <Box
          sx={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: { xs: '14px 20px', sm: '16px 32px' },
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            flexWrap: { xs: 'wrap', lg: 'nowrap' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}>
            <CookieOutlinedIcon sx={{ color: '#AF3241', fontSize: 28, mt: 0.5, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#333',
                  mb: 0.5,
                }}
              >
                Cookie Preferences
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '13px',
                  lineHeight: 1.6,
                  color: '#666',
                  mb: 1.5,
                }}
              >
                We use cookies to ensure basic functionality and to analyze user behavior
                to improve our service. You can choose which cookies to allow.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Chip
                    label="Required"
                    size="small"
                    sx={{
                      backgroundColor: '#e8e8e8',
                      color: '#666',
                      fontWeight: 500,
                      mt: 0.3,
                    }}
                  />
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#333', flex: 1, alignSelf: 'center' }}>
                    <strong>Necessary Cookies:</strong> Essential for authentication, session
                    management, and basic functionality
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Chip
                    label="Optional"
                    size="small"
                    sx={{
                      backgroundColor: '#fff3f4',
                      color: '#AF3241',
                      border: '1px solid #AF3241',
                      fontWeight: 500,
                      mt: 0.3,
                    }}
                  />
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#333', flex: 1, alignSelf: 'center' }}>
                    <strong>Analytics:</strong>{' '}
                    Help us understand how you use the site
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              flexShrink: 0,
              width: { xs: '100%', lg: 'auto' },
              alignContent: 'center',
            }}
          >
            <Button
              variant="text"
              onClick={handleOpenSettings}
              startIcon={<SettingsOutlinedIcon />}
              sx={{
                color: '#666',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: 500,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              Manage Preferences
            </Button>
            <Button
              variant="outlined"
              onClick={handleRejectOptional}
              sx={{
                borderColor: '#AF3241',
                color: '#AF3241',
                padding: '8px 20px',
                fontSize: '13px',
                fontWeight: 500,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: '#AF3241',
                  backgroundColor: '#fff3f4',
                },
              }}
            >
              Only Necessary
            </Button>
            <Button
              variant="contained"
              onClick={handleAcceptAll}
              sx={{
                backgroundColor: '#AF3241',
                color: '#fff',
                padding: '8px 28px',
                fontSize: '13px',
                fontWeight: 500,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: '#AF3241',
                  opacity: 0.9,
                },
              }}
            >
              Accept All
            </Button>
          </Box>
        </Box>
      </Paper>

      <CookieDetailModal
        open={settingsOpen}
        onClose={handleCloseSettings}
      />
    </>
  );
};

export default CookieBanner;